import React, { useState } from "react";
import { Trash2 } from "lucide-react";

export default function ArrayEditor({ items, onChange, label, accent = "yellow", testKey, capacity, itemActions }) {
  const [draft, setDraft] = useState("");
  const cls = accent === "magenta" ? "panel-magenta" : accent === "cyan" ? "panel-cyan" : "panel-yellow";
  const atCap = typeof capacity === "number" && items.length >= capacity;
  const setAt = (idx, patch) => onChange(items.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const removeAt = (idx) => onChange(items.filter((_, i) => i !== idx));
  const actions = Array.isArray(itemActions) ? itemActions : [];
  const add = () => {
    if (!draft.trim()) return;
    if (atCap) return;
    onChange([...items, { text: draft.trim() }]);
    setDraft("");
  };
  return (
    <div className={`panel ${cls} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-display uppercase font-black tracking-tight text-xl">{label}</div>
        <span className={`tape text-[10px] ${atCap ? "" : "tape-cyan"}`} data-testid={`${testKey}-count`}>
          {typeof capacity === "number" ? `${items.length}/${capacity}` : items.length}
        </span>
      </div>
      <div className="space-y-2 mb-3 max-h-[220px] overflow-y-auto pr-1" data-testid={`${testKey}-list`}>
        {items.length === 0 && <div className="font-mono text-xs text-[color:var(--text-muted)]">—</div>}
        {items.map((it, idx) => (
          <div key={idx} className="flex items-center gap-2 border-2 border-[color:var(--border-hard)] px-2 py-1">
            <input
              data-testid={`${testKey}-item-${idx}`}
              value={it.text || ""}
              onChange={(e) => setAt(idx, { text: e.target.value })}
              className="cy-input border-0 flex-1"
              style={{ minHeight: 36 }}
            />
            {actions.map((a, ai) => {
              if (a.visible && a.visible(it) === false) return null;
              const colorCls = a.color === "yellow"
                ? "cy-btn-solid-yellow"
                : a.color === "cyan"
                ? "cy-btn-cyan"
                : "cy-btn-magenta";
              return (
                <button
                  key={a.testKey || ai}
                  data-testid={`${testKey}-${a.testKey || `action${ai}`}-${idx}`}
                  onClick={() => a.onClick(it, idx)}
                  title={a.title || a.label}
                  className={`cy-btn ${colorCls} text-[10px] px-2 py-1 min-h-0`}
                >
                  {a.label || "GO"}
                </button>
              );
            })}
            <button
              aria-label="Delete"
              data-testid={`${testKey}-remove-${idx}`}
              onClick={() => removeAt(idx)}
              className="p-2 hover:bg-[color:var(--accent-magenta)] hover:text-[color:var(--bg-void)]"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={atCap ? "STORAGE FULL — buy a backpack" : `Add ${label.toLowerCase()}…`}
          disabled={atCap}
          className="cy-input disabled:opacity-50"
          data-testid={`${testKey}-input`}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(); }
          }}
        />
        <button data-testid={`${testKey}-add`} onClick={add} disabled={atCap} className="cy-btn text-xs disabled:opacity-50">ADD</button>
      </div>
    </div>
  );
}
